import React, { useEffect, useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import PropTypes from "prop-types";
import Breadcrumb from '../Breadcrumb';
import { logoutUser } from '../../actions/user';
import "./UserManagement.css"
import { USER_STATUS } from "../../constants/Enums"; 

const UserManagement = React.memo(
  ({ isFetching, data, count, fetchUserList, user_info, fetchUserInfo }) => {
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 200;

    const [filteredData, setFilteredData] = useState([]);
    const [filters, setFilters] = useState({
      login_id: "",
      user_name: ""
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    
    useEffect(() => {
      fetchUserInfo();
      fetchUserList();
    }, [fetchUserInfo, fetchUserList, location.state?.reloadAt]);

    //親から取得した data が更新されたら一覧を再設定
    useEffect(() => {
      setFilteredData(data);
    }, [data]);

    //親から取得した data が更新されたら一覧を再設定
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    const paginatedData = filteredData.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );

    //フィルター入力変更処理
    const onFilterChange = (e) => {
      const { name, value } = e.target;

      // 入力値の前スペースを除去
      const newFilters = { ...filters, [name]: value.trimStart() };
      setFilters(newFilters);

      // 入力を消した場合 → フィルター解除（元データに戻す）
      if (newFilters.login_id.trim() === "" && newFilters.user_name.trim() === "") {
        setFilteredData(data);
        setCurrentPage(1);
      }
    };

    //検索ボタン押下：フィルタリング実行
    const handleSearch = () => {
      const login = filters.login_id.trim();
      const name = filters.user_name.trim();

      const filtered = data.filter(user => {
        const matchLogin =
          login === "" || user.login_id.includes(login);

        const matchName =
          name === "" || user.user_name.includes(name);

        return matchLogin && matchName;
      });

      setFilteredData(filtered);
      setCurrentPage(1);
    };

    const isFiltered =
      filters.login_id.trim() !== "" ||
      filters.user_name.trim() !== "";

    /* -------------------------------
      ユーザーステータス整形（画面表示用）
      ------------------------------- */
    const renderUserStatus = useCallback(
      (item) => {
        const user_status = Object.values(USER_STATUS)
          .find(v => v.id === item.user_status);

        return (
          <span style={{ color: user_status?.hex || "#000" }}>
            {user_status?.text || "不明"}
          </span>
        );
      },
      []
    );

    return (
      <div id="Main">
        <div id="Contents">

          <Breadcrumb
            user={user_info?.admin_name}
            onLogout={() => dispatch(logoutUser())}
          />

          <div className="user-master-layout">

            {/* HEADER CARD */}
            <div className="user-header-card">
              <h2 className="user-title">ユーザー一覧</h2>

              {/* 検索フィルター */}
              <div className="user-filter-row">
                <div className="user-filter-form">
                  <div className="form-row">
                    <label>ログインID</label>
                    <input
                      type="text"
                      name="login_id"
                      value={filters.login_id}
                      onChange={onFilterChange}
                      className="filter-input"
                    />
                  </div>

                  <div className="form-row">
                    <label>ユーザー名</label>
                    <input
                      type="text"
                      name="user_name"
                      value={filters.user_name}
                      onChange={onFilterChange}
                      className="filter-input"
                    />
                  </div>
                </div>
                
                <div className="user-filter-button">
                    <button className="filter-search-btn" onClick={handleSearch}>
                      表示
                    </button>
                </div>

              </div>
                            
              <button className="user-register-btn" onClick={() => navigate("/user-list/create")}>新規登録</button>

              <div className="table-container">
                {/* TABLE */}
                <table className="user-table">
                  <thead>
                    <tr>
                      <th className="colLoginId">ログインID</th>
                      <th className="colUserName">ユーザー名</th>
                      <th className="colStatus">ステータス</th>
                      <th className="colEdit">編集</th>
                    </tr>
                  </thead>

                  <tbody>
                    {!isFetching && paginatedData.map((item, index) => (
                      <tr key={item.user_id || index}>
                        <td>{item.login_id}</td>
                        <td>{item.user_name}</td>
                        <td>{renderUserStatus(item)}</td>       
                        <td>
                          <button
                            className="user-edit-btn"
                            onClick={() => navigate(`/user-list/edit/${item.user_id}`)}
                          >
                            編集
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            </div>

            {/* FOOTER */}
            <div className="footer-wrapper">
              <div className="user-footer">件数：{isFiltered ? filteredData.length : count}</div>
              <div className="pagination-center">
                <div className="pagination-container">
                    <button
                      className="page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(1)}
                    >
                      ≪
                    </button>

                    <button
                      className="page-btn"
                      disabled={currentPage === 1}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                    >
                      ＜
                    </button>

                    {/* Page numbers */}
                    {[...Array(totalPages)].map((_, idx) => {
                      const page = idx + 1;
                      return (
                        <button
                          key={page}
                          className={`page-number-btn ${page === currentPage ? "active" : ""}`}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </button>
                      );
                    })}

                    <button
                      className="page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                    >
                      ＞
                    </button>

                    <button
                      className="page-btn"
                      disabled={currentPage === totalPages}
                      onClick={() => setCurrentPage(totalPages)}
                    >
                      ≫
                    </button>

                </div>
              </div>

              <div className="footer-empty"></div>
            </div>

          </div>
        </div>
        <div className={`overlay ${isFetching ? 'active' : ''}`}>
          <div className={`spinner ${isFetching ? 'active' : ''}`}></div>
        </div>
      </div>
    );
  }
);

UserManagement.displayName = "UserManagement";    // fix eslint(react/display-name)

UserManagement.propTypes = {
    isFetching: PropTypes.bool.isRequired,
    user_info: PropTypes.object,
    data: PropTypes.array,
    count: PropTypes.number,
    error: PropTypes.any,
    fetchUserList: PropTypes.func.isRequired,
    fetchUserInfo: PropTypes.func.isRequired,
};

export default UserManagement;
